import { on } from '@ember/modifier';
import { type Registry as Services, service } from '@ember/service';
import type { SafeString } from '@ember/template';
import { htmlSafe } from '@ember/template';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import GeneralRateIcons from 'lumen/components/general/rate-icons';
import HotelRatesTablesCellsTdRate, {
  type HotelRatesTablesCellsTdRateSignature,
  type TdRateCustomColumn,
} from 'lumen/components/hotel/rates/tables/cells/td-rate';
import HotelTooltipsTooltipRatesTable from 'lumen/components/hotel/tooltips/tooltip-rates-table';
import type { PeriodComparisonRate } from 'lumen/models/period';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  COMPARE_DIFFERENCE_VISUALISATION_MAPPING,
  COMPARE_MESSAGE_TO_ICON_NAME,
  COMPARE_MESSAGES,
  COMPARE_TYPE_TO_MESSAGE,
  type CompareType,
  COMPARETYPES,
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
  type MembershipType,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  PLATFORM_TRANSLATION_KEYS,
  RATE_PARAMS,
  RATE_PARAMS_TO_TRANSLATION_KEYS,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import formatDate from 'lumen/utils/format-local-date';
import { isDateCompare } from 'lumen/utils/is-comparing-rates';

interface TdRateCompareCustomColumn extends TdRateCustomColumn {
  isLoading: boolean;
}

export interface HotelRatesTablesCellsTdRateCompareSignature extends HotelRatesTablesCellsTdRateSignature {
  Args: HotelRatesTablesCellsTdRateSignature['Args'] & {
    column: TdRateCompareCustomColumn;
  };
}

export default class HotelRatesTablesCellsTdRateCompare extends HotelRatesTablesCellsTdRate<HotelRatesTablesCellsTdRateCompareSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare rates: Services['rates'];

  // Returns the translation for "Change" or "Difference"
  get compareTextPlaceholder(): string {
    return this.compareType === COMPARETYPES.DATE
      ? this.intl.t('rates.changes.change_tooltip')
      : this.intl.t('general.difference');
  }

  get layoutConditionalClassNames(): string {
    const { layout, textAlign } = this.args.column;
    const alignment = textAlign === 'left' ? 'items-start' : 'items-end';
    const justify = textAlign === 'left' ? 'justify-start' : 'justify-end';
    const isRowLayout = layout === 'side-by-side';

    return isRowLayout
      ? `td-rate-compare--row ${justify}`
      : `td-rate-compare--column ${alignment}`;
  }

  get layoutConditionalMargins(): string {
    const isColumnLayout = this.args.column.layout === 'separate-row';
    return isColumnLayout ? 'mr-500' : '';
  }

  /**
   * Returns an object describing the rate changes
   *
   * Table:
   * value: rate change displayed next to the base rate
   * highestPrioChangeIconName: contains the icon with the highest priority
   *
   * Tooltip:
   * currentSelection: e.g. Lowest
   * versusSelection: e.g. Best Flex
   * currentTooltipRate: the rate of to the current selection
   * versusTooltipRate: the rate of to the compare value
   * currentRateTypeString: e.g Refundable, Breakfast
   * versusRateTypeString: e.g Non Refundable, No breakfast
   * versusRoomName: the room name of the compare value
   *
   * Difference row:
   * showDifferenceRow: whether to show a row in the tooltip with the rate change details
   * class: css class for rateDifference
   * rateDifference: the absolute and relative difference between the rates
   * isOutlier: true if the rate change is unusual
   * roomNameChangeText: explanation of the room change
   *
   * Warning row:
   * showWarningRow: boolean flag determining whether to show the warning row
   * extractDatetimeDifferenceString: time difference (in hours) wrapped in string
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateChangeValueObj() {
    // Check if there is a rate object with comparison rates (camelcase for market, snakecase for hotel)
    if (
      !(
        this.rateObject &&
        (this.rateObject?.comparisonRates || this.rateObject?.comparison_rates)
      )
    ) {
      return null;
    }

    const arrRateChanges =
      this.rateObject?.comparisonRates || this.rateObject?.comparison_rates;
    // Check if ratechanges array contains an object for the selected rate change number
    if (arrRateChanges && arrRateChanges.length === 1) {
      const { compareType, compareValue } = this;
      if (!compareType || !compareValue) {
        return null;
      }
      const ratechange = arrRateChanges[0] as PeriodComparisonRate;
      const compareRate = ratechange.value;
      const compareMessage = ratechange.message;
      const rateToday = this.rateObject?.value;
      const messageToday = this.rateObject?.message;
      const currentRoomName =
        this.rateObject?.room_name || this.rateObject?.roomName;
      const isCompareMissing = compareMessage === 'general.missing';

      const extractDatetimeDifference = Math.abs(
        dayjs(this.rateObject?.extract_datetime).diff(
          dayjs(ratechange.extract_datetime),
          'hour',
        ),
      );
      const extractDatetimeDifferenceString = this.intl.t(
        'rates.compare.warning.extract_datetime_difference',
        { extractDatetimeDifference },
      );
      // Only show the warning row if
      // 1. The compare is not missing
      // 2. The time difference is larger than one hour
      // 3. The user is not comparing to a past date
      const showWarningRow =
        !isCompareMissing &&
        extractDatetimeDifference > 1 &&
        !isDateCompare(compareType);

      let currentSelection = '';
      let versusSelection = '';
      switch (compareType) {
        case COMPARETYPES.RATE_PARAM: {
          currentSelection = this.intl.t(
            RATE_PARAMS_TO_TRANSLATION_KEYS[this.rateParam],
          );
          versusSelection = this.intl.t(
            RATE_PARAMS_TO_TRANSLATION_KEYS[compareValue as RateParam],
          );
          break;
        }
        case COMPARETYPES.MEMBERSHIPTYPE: {
          currentSelection = this.intl.t(
            MEMBERSHIPTYPE_TRANSLATION_KEYS[this.membershiptype],
          );
          versusSelection = this.intl.t(
            MEMBERSHIPTYPE_TRANSLATION_KEYS[
              Number(compareValue) as MembershipType
            ],
          );
          break;
        }
        case COMPARETYPES.MEALTYPE: {
          currentSelection = this.intl.t(
            MEALTYPE_TRANSLATION_KEYS[this.mealPlan],
          );
          versusSelection = this.intl.t(
            MEALTYPE_TRANSLATION_KEYS[Number(compareValue) as MealTypeValues],
          );
          break;
        }
        case COMPARETYPES.LOS:
          currentSelection = `LOS${this.los}`;

          versusSelection = `LOS${compareValue}`;
          break;
        case COMPARETYPES.PERSONS:
          currentSelection = this.intl.t('general.guest', {
            count: this.args.column.requestParams.maxPersons,
          });
          versusSelection = this.intl.t('general.guest', {
            count: compareValue,
          });
          break;
        case COMPARETYPES.PLATFORM:
          currentSelection = this.intl.t(
            PLATFORM_TRANSLATION_KEYS[this.platform],
          );
          versusSelection = this.intl.t(
            PLATFORM_TRANSLATION_KEYS[Number(compareValue) as Platform],
          );
          break;
        case COMPARETYPES.SWLY:
          currentSelection = this.intl.t('general.today');
          versusSelection = `${
            isCompareMissing
              ? this.intl.t('general.missing')
              : formatDate(
                  this.hotelAndMarketService.toLocalTime(ratechange.from_date),
                )
          } (${this.intl.t('revenue_insight.swly_short')})`;
          break;
        case COMPARETYPES.ROOMTYPE: {
          // Roomtype mapping logic for custom roomtype names
          const roomTypeMapping =
            this.hotelAndMarketService.hotel.roomtype?.roomtypeMapping;
          const currentRoomTypeObject = roomTypeMapping.find(
            (roomType) => roomType.roomtype_id === this.roomType,
          );
          const versusRoomTypeObject = roomTypeMapping.find(
            (roomType) => roomType.roomtype_id === compareValue,
          );
          if (currentRoomTypeObject) {
            currentSelection = currentRoomTypeObject.roomtype_name;
          }
          if (versusRoomTypeObject) {
            versusSelection = versusRoomTypeObject.roomtype_name;
          }

          // Default room types need to be translated, custom room types don't
          if (
            currentRoomTypeObject &&
            !currentRoomTypeObject.roomtype_id.startsWith('c_')
          ) {
            currentSelection = this.intl.t(
              `rates.roomtype.${currentRoomTypeObject.roomtype_name}`,
            );
          }
          if (
            versusRoomTypeObject &&
            !versusRoomTypeObject.roomtype_id.startsWith('c_')
          ) {
            versusSelection = this.intl.t(
              `rates.roomtype.${versusRoomTypeObject.roomtype_name}`,
            );
          }

          break;
        }
        default:
          currentSelection = this.intl.t('general.today');
          versusSelection = formatDate(dayjs(compareValue)).toString();
          break;
      }

      const differenceVisualisation =
        COMPARE_DIFFERENCE_VISUALISATION_MAPPING[compareType as CompareType];

      if (typeof compareRate === 'number' && typeof rateToday === 'number') {
        const noMessage = compareMessage === 'rates.compare.unavailable';

        if (rateToday === 0 && compareRate > 0) {
          // if rateToday is sold out, and compareRate is a number
          // => display the old rate value between brackets
          return {
            value: htmlSafe(
              `[${this.hotelAndMarketService.formatRateChange(
                compareRate,
                ratechange.currency,
              )}]`,
            ),
            // Tooltip data
            currentSelection,
            versusSelection,
            currentTooltipRate: this.intl.t(messageToday),
            versusTooltipRate: this.hotelAndMarketService.formatRateChange(
              compareRate,
              ratechange.currency,
            ),
            versusRateTypeString: this.rates.getRateTypeString(ratechange),
            currentRoomName,
            versusRoomName: ratechange.room_name,
            // Difference row
            showDifferenceRow: false,
            // Warning row
            showWarningRow,
            extractDatetimeDifferenceString,
          };
        }

        if (
          (rateToday > 0 && compareRate === 0) ||
          (rateToday === compareRate &&
            compareRate === 0 &&
            messageToday !== compareMessage &&
            compareMessage !== undefined)
        ) {
          if (!noMessage) {
            let currentTooltipRate: string | SafeString = '';

            // eslint-disable-next-line max-depth
            if (messageToday) {
              currentTooltipRate = this.intl.t(messageToday);
            } else {
              currentTooltipRate =
                this.hotelAndMarketService.formatRateObject(this.rateObject) ??
                '';
            }

            return {
              value: `[${this.intl.t(compareMessage)}]`,
              // Tooltip data
              currentSelection,
              versusSelection,
              currentTooltipRate,
              versusTooltipRate: this.intl.t(compareMessage),
              currentRateTypeString: this.rates.getRateTypeString(
                this.rateObject,
              ),
              currentRoomName,
              versusRoomName: '',
              // Difference row
              showDifferenceRow: false,
              // Warning row
              showWarningRow,
              extractDatetimeDifferenceString,
            };
          }
        }

        if (!noMessage && Math.abs(rateToday - compareRate) >= 1) {
          const formattedAbsoluteDifference =
            this.hotelAndMarketService.formatRate(
              ratechange.difference_absolute,
              {
                includeCurrencySymbol: false,
                longFormat: false,
                includeSign: true,
              },
            );
          const formattedAbsoluteDifferenceWithSymbol =
            this.hotelAndMarketService.formatRateChange(
              ratechange.difference_absolute,
              ratechange.currency,
              { longFormat: false, includeSign: true },
            );

          let formattedRelativeDifference = null;
          const relativeDifferencePercent =
            ratechange.difference_relative * 100;
          if (Math.abs(relativeDifferencePercent) > 1) {
            // Minus sign is already present, plus sign is not
            formattedRelativeDifference =
              ratechange.difference_relative > 0 ? '+' : '';
            formattedRelativeDifference += `${Math.round(relativeDifferencePercent)}%`;
          } else {
            formattedRelativeDifference = '< 1%';
          }

          const ignoredCompareMessages: string[] = [];
          if (compareType === COMPARETYPES.RATE_PARAM) {
            const isComparingBestFlex =
              this.rateParam === RATE_PARAMS.BEST_FLEX ||
              compareValue === RATE_PARAMS.BEST_FLEX;
            const isComparingLowestFlex =
              this.rateParam === RATE_PARAMS.FLEXIBLE ||
              compareValue === RATE_PARAMS.FLEXIBLE;
            // eslint-disable-next-line max-depth
            if (isComparingBestFlex) {
              // Don't show Best Flex icon when comparing with Best Flex rates (best_flex is always different)
              ignoredCompareMessages.push(COMPARE_MESSAGES.BAR);
            }
            // eslint-disable-next-line max-depth
            if (isComparingLowestFlex || isComparingBestFlex) {
              // Don't show Lowest Flex icon when comparing Lowest Flex or Best Flex rates (cancellable is always different)
              ignoredCompareMessages.push(COMPARE_MESSAGES.CANCELLABLE);
            }
            // Notice there is no check for Third Party rates: we always want to show the rate_type change even when comparing
          } else if (
            compareType === COMPARETYPES.MEMBERSHIPTYPE ||
            compareType === COMPARETYPES.ROOMTYPE
          ) {
            // Don't show Membership/Roomtype icon when comparing memberships/roomtypes rates
            ignoredCompareMessages.push(COMPARE_TYPE_TO_MESSAGE[compareType]);
          }

          let highestPrioChangeIconName = null;
          let hasRoomTypeChange = false;
          const compareMessages = ratechange.compare_messages;

          if (compareMessages?.length) {
            // Ignore redundant compare indicators (e.g. don't show roomtype change icon when comparing roomtypes)
            const compareMessage = compareMessages.find((message) => {
              return !ignoredCompareMessages.includes(message);
            }) as keyof typeof COMPARE_MESSAGE_TO_ICON_NAME | undefined;

            if (compareMessage) {
              // Show the icon related to the first of the remaining messages
              highestPrioChangeIconName =
                COMPARE_MESSAGE_TO_ICON_NAME[compareMessage];
            }

            hasRoomTypeChange = compareMessages.includes(
              COMPARE_MESSAGES.ROOMTYPE,
            );
          }

          const currentRateTypeString = this.rates.getRateTypeString(
            this.rateObject,
          );
          const versusRateTypeString = this.rates.getRateTypeString(ratechange);

          return {
            value:
              differenceVisualisation === 'percentage'
                ? formattedRelativeDifference
                : formattedAbsoluteDifference,
            highestPrioChangeIconName,
            // Tooltip data
            currentSelection,
            versusSelection,
            currentTooltipRate: this.hotelAndMarketService.formatRateObject(
              this.rateObject,
            ),
            versusTooltipRate: this.hotelAndMarketService.formatRateChange(
              compareRate,
              ratechange.currency,
            ),
            currentRateTypeString,
            versusRateTypeString,
            hasRateTypeColumn:
              currentRateTypeString !== '' || versusRateTypeString !== '',
            currentRoomName,
            versusRoomName: ratechange.room_name,
            // Difference row
            showDifferenceRow: true,
            class:
              ratechange.difference_absolute > 0
                ? 'text-success-default'
                : 'text-critical-default',
            rateDifference: htmlSafe(
              `${formattedAbsoluteDifferenceWithSymbol} / ${formattedRelativeDifference}`,
            ),
            isOutlier:
              Math.abs(ratechange.outlier_score) > 0 && !hasRoomTypeChange, // Don't mark an outlier if the room type has changed
            roomChangeText: hasRoomTypeChange
              ? this.intl.t('rates.changes.room_type_change')
              : '',
            // Warning row data
            showWarningRow,
            extractDatetimeDifferenceString,
          };
        }
      }
    }

    return null;
  }

  // Returns the translation for "Rate" or "Average daily rate"
  get rateTextPlaceholder(): string {
    return this.compareType === COMPARETYPES.LOS
      ? this.intl.t('rates.average_daily_rate')
      : this.intl.t('rates.rate');
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    {{#unless @column.isLoading}}
      <div
        class="text-200--regular py-200 pl-200 tabular-nums"
        role="button"
        {{on "dblclick" this.onDoubleClick}}
      >
        {{#if this.isPast}}
          <div class={{this.layoutConditionalClassNames}}>
            <div
              class="td-past truncate
                {{if this.isPositive 'text-success-default'}}
                {{if this.isNegative 'text-critical-default'}}
                {{unless this.showRateChanges this.layoutConditionalMargins}}"
            >
              <p>{{this.rateValue}}</p>

              {{#if this.showRateChanges}}
                <GeneralRateIcons
                  @leaveSpaceWhenEmpty={{true}}
                  @mealPlan={{this.mealPlan}}
                  @otaId={{this.otaId}}
                  @rate={{this.rateObject}}
                  @rateParam={{this.rateParam}}
                  @size="small"
                />
              {{/if}}
              <HotelTooltipsTooltipRatesTable
                @date={{this.day.date}}
                @fetchOtherLosRates={{true}}
                @hotel={{this.hotelAndMarketService.hotel}}
                @isAllChannels={{this.isAllChannels}}
                @ownHotelRate={{this.ownHotelRate}}
                @requestParams={{@column.requestParams}}
                @value={{this.ratesTooltipValues}}
              />
            </div>
            <div
              class="td-right-value td-past
                {{this.layoutConditionalMargins}}
                flex items-center gap-100 truncate
                {{if this.rateChangeValueObj.isOutlier 'is-outlier'}}
                {{this.rateChangeValueObj.class}}"
            >
              {{#if this.rateChangeValueObj.value}}
                {{this.rateChangeValueObj.value}}
              {{else}}
                &nbsp;
              {{/if}}
            </div>
          </div>
        {{else}}
          <div class={{this.layoutConditionalClassNames}}>
            <div
              class="{{if this.isToday 'td-today'}}
                {{this.layoutConditionalMargins}}"
            >
              {{#if this.redirectUrl}}
                <a
                  class="underline-on-hover truncate"
                  href={{this.redirectUrl}}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{this.rateValue}}
                </a>
              {{else}}
                <p class="truncate">{{this.rateValue}}</p>
              {{/if}}
              <HotelTooltipsTooltipRatesTable
                @date={{this.day.date}}
                @fetchOtherLosRates={{true}}
                @hotel={{this.hotelAndMarketService.hotel}}
                @isAllChannels={{this.isAllChannels}}
                @ownHotelRate={{this.ownHotelRate}}
                @requestParams={{@column.requestParams}}
                @value={{this.ratesTooltipValues}}
              />
            </div>

            <div
              class="td-right-value flex items-center gap-100 truncate
                {{if this.rateChangeValueObj.isOutlier 'is-outlier'}}
                {{unless
                  this.rateChangeValueObj.highestPrioChangeIconName
                  this.layoutConditionalMargins
                }}
                {{this.rateChangeValueObj.class}}"
            >
              {{#if this.rateChangeValueObj.value}}
                {{this.rateChangeValueObj.value}}
              {{else}}
                &nbsp;
              {{/if}}
              {{#if this.rateChangeValueObj.highestPrioChangeIconName}}
                <div class="cell-rate-changes__prism-icon">
                  <PrismIcon
                    {{! @glint-expect-error: Incorrect type }}
                    @name={{this.rateChangeValueObj.highestPrioChangeIconName}}
                    @size="small"
                  />
                </div>
              {{/if}}

              {{#if this.rateChangeValueObj.value}}
                <EmberTooltip
                  @delay={{25}}
                  @side="right"
                  @tooltipClass="base-tooltip rates-table-tooltip"
                >
                  <div class="rates-table-tooltip-container">
                    <table cellspacing="0">
                      <thead>
                        <tr>
                          <th>{{t "general.compare"}}</th>
                          <th>{{this.rateTextPlaceholder}}</th>
                          <th>{{t "rates.changes.roomtype"}}</th>
                          {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                            <th>{{t "rates.type"}}</th>
                          {{/if}}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{{this.rateChangeValueObj.currentSelection}}</td>
                          <td
                          >{{this.rateChangeValueObj.currentTooltipRate}}</td>
                          <td>{{this.rateChangeValueObj.currentRoomName}}</td>
                          {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                            <td
                            >{{this.rateChangeValueObj.currentRateTypeString}}</td>
                          {{/if}}
                        </tr>

                        {{#if this.rateChangeValueObj.showDifferenceRow}}
                          <tr>
                            <td>{{this.compareTextPlaceholder}}</td>
                            <td>
                              <span class={{this.rateChangeValueObj.class}}>
                                {{this.rateChangeValueObj.rateDifference}}
                              </span>
                            </td>
                            <td>{{this.rateChangeValueObj.roomChangeText}}</td>
                            {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                              <td></td>
                            {{/if}}
                          </tr>
                        {{/if}}

                        <tr>
                          <td>{{this.rateChangeValueObj.versusSelection}}</td>
                          <td>{{this.rateChangeValueObj.versusTooltipRate}}</td>
                          <td>{{this.rateChangeValueObj.versusRoomName}}</td>
                          {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                            <td
                            >{{this.rateChangeValueObj.versusRateTypeString}}</td>
                          {{/if}}
                        </tr>
                      </tbody>
                    </table>

                    {{#if this.rateChangeValueObj.showWarningRow}}
                      <div class="tooltip-footer flex items-center gap-100">
                        <PrismIcon @name="sync-disabled" @size="small" />
                        {{this.rateChangeValueObj.extractDatetimeDifferenceString}}
                      </div>
                    {{/if}}

                  </div>
                </EmberTooltip>
              {{/if}}
            </div>
          </div>
        {{/if}}
      </div>
    {{/unless}}
  </template>
}
